import type { UseFieldListenersOptions } from '../types'
import type { FieldContext } from 'vee-validate'

/**
 * Applies a generic validation schema to input fields.
 *
 * The experience is:
 * Validate on Change/Blur initially (when the user leaves the control), let’s call this lazy mode.
 * If the field is invalid, switch the validation to validate on input (when the user types), let’s call this aggressive mode.
 * If the field is valid, go back to “lazy” mode, otherwise, be “aggressive”.
 *
 * Inspired by the following part of vee-validate [docs](https://vee-validate.logaretm.com/v4/guide/composition-api/custom-inputs/#other-validation-providers)
 *
 * **IMPORTANT**
 *
 * Set `validateOnValueUpdate` to `false` when using `useField`. @see UIInput.vue for details.
 *
 * @param context The result of calling `useField`
 * @param options @see UseFieldListenersOptions
 * @returns An object that can be added directly to an input with `v-on`
 */
export const useFieldListeners = (context: FieldContext<any>, options: UseFieldListenersOptions = {}) => {
    const {
        enableBlur = true,
        enableChange = true,
        enableInput = true,
        forceValidateOnInput = false,
        forceValidateOnBlur = false,
        forceValidateOnChange = false,
    } = options

    const listeners: {
        blur?: (event: any) => void
        change?: (event: any) => void
        input?: (evt: any) => void
    } = {}

    if (enableBlur) {
        listeners.blur = (evt: any) => {
            context.handleBlur(evt, forceValidateOnBlur)
        }
    }

    if (enableChange) {
        listeners.change = (evt: any) => {
            context.handleChange(evt, forceValidateOnChange)
        }
    }

    if (enableInput) {
        listeners.input = (evt: any) => {
            const shouldValidate = forceValidateOnInput || !!context.errorMessage.value

            context.handleChange(evt, shouldValidate)
        }
    }

    return listeners
}
